import type { SupportedLocale } from "../i18n/locales.ts";

/** Each artwork role has its own locale map and a mandatory English fallback. */
export type BundleOfferImages = Readonly<
    { en: string } & Partial<Record<SupportedLocale, string>>
>;

/** Coordinates are CSS pixels in the artwork's reference canvas. */
export interface BundleOfferFrame {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface BundleOfferPrizeArtwork {
    image: string;
    frame: BundleOfferFrame;
    amountFrame: BundleOfferFrame;
}

export interface BundleOfferArtwork {
    /** Layout width; controls may overhang the background border. */
    width: number;
    height: number;
    /** Physical screen-edge gaps at the 440px reference viewport. */
    sideInsets?: { left: number; right: number };
    header: { images: BundleOfferImages; frame: BundleOfferFrame };
    background: { images: BundleOfferImages; frame: BundleOfferFrame };
    close: { image: string; frame: BundleOfferFrame };
    purchase: BundleOfferFrame;
}

export interface BundleOfferPrize {
    key: string;
    /** Integer decimal string from the validated server reward. */
    amount: string;
    /** Localized, full reward description for assistive technology. */
    label: string;
    artwork: BundleOfferPrizeArtwork;
}

export function compactBundleAmount(amount: string, locale: string): string {
    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
    })
        .formatToParts(BigInt(amount))
        .map((part) =>
            locale === "en" && part.type === "compact" && part.value === "K"
                ? "k"
                : part.value,
        )
        .join("");
}

export function bundleDeadline(
    expiresAt: string | null | undefined,
): number | null {
    if (expiresAt == null) return null;
    const deadline = Date.parse(expiresAt);
    // An invalid supplied deadline must not turn an expiring offer into an unlimited one.
    return Number.isFinite(deadline) ? deadline : 0;
}
